// FIRST import, before anything that might touch the polyfilled APIs — the
// livekit chunk calls crypto.randomUUID unguarded, and the lazy Excalidraw tree
// uses Object.hasOwn / .at() / replaceAll / structuredClone.
import './polyfills'

import '@livekit/components-styles'
// Order is load-bearing: reset first, then the theme. When styles.css came last
// its generic .primary-button rule won over the theme's, quietly shrinking the
// Join CTA below the touch size it was designed for.
import './styles.css'
import './theme.css'

import { setLogLevel } from 'livekit-client'
import React from 'react'
import { createRoot } from 'react-dom/client'

import { App } from './App'
import { AppErrorBoundary } from './AppErrorBoundary'
import { applyDocumentLocale } from './i18n'
import { browserSupported, renderUnsupported } from './support'

if (import.meta.env.PROD)
  setLogLevel('warn')

  // Excalidraw fetches its fonts from this base. We self-host them (see
  // scripts/copy-excalidraw-fonts.mjs) because the default is a hardcoded CDN our
  // CSP blocks — which broke the board's text tool and stalled first paint. Set
  // before any board code loads, i.e. here at boot rather than in the lazy chunk.
;(window as unknown as { EXCALIDRAW_ASSET_PATH: string }).EXCALIDRAW_ASSET_PATH =
  '/excalidraw-assets/'

// Direction and language are set before first paint so LiveKit's prefabs and
// our own layout mirror correctly for Arabic rather than flipping mid-render.
applyDocumentLocale()

// Gate BEFORE React mounts: a browser below the WebRTC floor gets a clear
// bilingual "update your browser" screen instead of a white page or a cryptic
// failure at the join click.
const rootEl = document.getElementById('root')!
// The real gate is public/support-gate.js — a classic script that runs before
// any module and paints the message even if a chunk throws on an old browser.
// This in-module check is the belt to that braces: it catches the case where
// the gate file itself failed to load.
if (
  (window as unknown as { __MEET_UNSUPPORTED__?: boolean }).__MEET_UNSUPPORTED__ ||
  !browserSupported()
) {
  renderUnsupported(rootEl)
} else {
  mount()
}

function mount() {
  createRoot(rootEl).render(
    <React.StrictMode>
      <AppErrorBoundary>
        <App />
      </AppErrorBoundary>
    </React.StrictMode>,
  )
}
